import { AnchorButton, Button, InputGroup, Intent, Position, Tag } from '@blueprintjs/core';
import { Tooltip2 } from '@blueprintjs/popover2';
import { push as changeLocation } from 'connected-react-router';
import { flowRight } from 'lodash';
import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import { connect, useDispatch, useSelector } from 'react-redux';
import { useRouteMatch } from 'react-router-dom';
import { styled, Button as M1Button } from '@moleculeone/ui';

import { withDataActions, withSelectability } from '@f/components/higher-order';
import BatchSearchStatusTag from '@f/components/shared/batch-search-status-tag/BatchSearchStatusTag';
import CreditConsumptionIndicator from '@f/components/shared/credit-consumption-indicator/CreditConsumptionIndicator';
import RemovalTooltip from '@f/components/shared/removal-tooltip/RemovalTooltip';
import { SearchDetails } from '@f/components/shared/search-details/SearchDetails';
import Table from '@f/components/shared/table/Table';
import Toolbar from '@f/components/shared/toolbar/Toolbar';
import { useConfig, useCreditOverview } from '@f/hooks';
import { findRequests, removeRequests } from '@f/services/batch-search/batch-search.actions';
import { useIsDebugModeOn } from '@f/hooks/useIsDebugModeOn';

import cls from './BatchSearchRequestListPage.module.css';
import { BatchSearchRequestName } from './components/BatchSearchRequestName';
import { ShareButton } from './components/ShareButton';

const SearchActions = styled('div', {
  display: 'flex',
  alignItems: 'center',
  gap: '$1',
});

const DebugButton = styled(M1Button, {
  fontFamily: '$mono',
});

function RemoveButton(props) {
  const { item, onFindNextItems, ...restProps } = props;

  const dispatch = useDispatch();

  const renderTarget = props => (
    <Button {...restProps} minimal icon="trash" onClick={props.onClick} />
  );

  const onRemove = async () => {
    await dispatch(removeRequests([item.id]));
    onFindNextItems(true);
  };

  return <RemovalTooltip renderTarget={renderTarget} onRemove={onRemove} />;
}

function BatchRequestListPage(props) {
  const {
    items,
    pagination,
    findNextItems,
    selectionMap,
    toggleItemSelection,
    toggleAllItemsSelection,
    deselectAllItems,
    removeItems,
  } = props;

  const isDebugModeOn = useIsDebugModeOn();
  const config = useConfig();
  const creditOverview = useCreditOverview();
  const dispatch = useDispatch();
  const match = useRouteMatch();

  const { isFindingRequests } = useSelector(state => {
    const requestState = state.batchSearch.request;
    const isFindingRequests = requestState.find.processing > 0;

    return {
      isFindingRequests,
    };
  });

  const [nameFilter, setNameFilter] = useState('');
  const [form, setForm] = useState({ name: '' });
  const [hoveredRow, setHoveredRow] = useState(null);

  const isRowSelected = item => {
    return Boolean(selectionMap[item.id]);
  };

  const handleHover = index => {
    setHoveredRow(index);
  };

  const handleMouseLeave = () => {
    setHoveredRow(null);
  };

  const onFindNextItems = async (flush = false) => {
    const query = {
      where: {
        isRemoved: false,
      },
      orderBy: [
        {
          createdAt: 'desc',
        },
      ],
    };

    if (nameFilter) {
      query.where.name = {
        contains: nameFilter,
      };
    }

    return findNextItems(query, { flush });
  };

  const onNavigateToItem = item => {
    dispatch(changeLocation(`${match.path}/${item.id}`));
  };

  const onNameChange = event => {
    setForm({
      ...form,
      name: event.target.value,
    });
  };

  const onSearchByName = async event => {
    event.preventDefault();

    setNameFilter(form.name);
    onFindNextItems(true);
  };

  const onCreateBatch = async () => {
    dispatch(changeLocation(`${match.path}/new`));
  };

  const onRemoveSelected = async () => {
    const nonRemovableItems = items
      .filter(item => item.shareInfo.sharedWithMe)
      .map(item => item.id);
    const selectedIds = Object.keys(selectionMap)
      .filter(id => selectionMap[id])
      .filter(id => !nonRemovableItems.includes(id));
    await removeItems(selectedIds);

    deselectAllItems();
  };

  const schema = [
    {
      label: 'STATUS',
      renderer: item => (
        <BatchSearchStatusTag large minimal status={item.status} removed={item.isRemoved} />
      ),
    },
    {
      label: 'NAME',
      grow: true,
      renderer: item => <BatchSearchRequestName batch={item} linked />,
      cellProps: {
        className: cls.primary,
      },
    },
    {
      label: 'SIZE',
      grow: true,
      renderer: 'size',
    },
    {
      label: 'CONSUMED CREDITS',
      grow: true,
      renderer: item =>
        item.creditsDue != null ? BigInt(item.creditsDue).toLocaleString() : 'n/a',
    },
    {
      label: 'CREATED AT',
      grow: true,
      renderer: item => new Date(item.createdAt).toLocaleString(),
    },
    {
      label: '',
      renderer: item => {
        return (
          <SearchActions onClick={e => e.stopPropagation()}>
            <SearchDetails
              search={item}
              tooltipPosition={Position.LEFT}
              triggerElement={<Button icon="small-info-sign" minimal />}
            />

            {item.shareInfo.sharedWithMe ? (
              <Tooltip2 content={`by ${item.shareInfo.ownerEmail}`} placement={Position.BOTTOM}>
                <Tag intent={Intent.PRIMARY}>Shared</Tag>
              </Tooltip2>
            ) : (
              <>
                <Tooltip2 content="Share" placement={Position.BOTTOM}>
                  <ShareButton item={item} />
                </Tooltip2>
                <Tooltip2 content="Remove" placement={Position.BOTTOM}>
                  <RemoveButton
                    item={item}
                    onFindNextItems={onFindNextItems}
                    data-cy="button-search-remove-one"
                  />
                </Tooltip2>
              </>
            )}

            {isDebugModeOn && (
              <DebugButton
                text="View hypertrees"
                size="small"
                weight="minimal"
                theme="purple"
                icon="open_in_new"
                onClick={() => {
                  window.open(
                    `${config.hypertreeUrl}/search/${item.id}?importFrom=${window.location.origin}`,
                    '_blank'
                  );
                }}
              />
            )}
          </SearchActions>
        );
      },
    },
  ];

  useEffect(() => {
    onFindNextItems(true);
  }, []);

  const { total, start, size } = pagination;
  const docsUrl = `${config.hubUrl}/docs/retro/`;

  return (
    <div className={cls.page}>
      <Toolbar>
        <Toolbar.Title>Searches</Toolbar.Title>
        <Toolbar.Item>
          <AnchorButton
            large
            minimal
            icon="manual"
            text="API User Guide"
            href={docsUrl}
            target="_blank"
          />
        </Toolbar.Item>

        <Toolbar.Item light alignRight className={cls.lastToolbarItem}>
          <CreditConsumptionIndicator />

          <Button
            large
            intent={Intent.PRIMARY}
            disabled={!creditOverview || creditOverview.balance <= 0n}
            text="Start a new search"
            icon="search-around"
            onClick={onCreateBatch}
          />
        </Toolbar.Item>
      </Toolbar>

      <div className={cls.listActions}>
        <Button
          large
          minimal
          disabled={!props.selectionMode || isFindingRequests}
          text="Remove"
          icon="trash"
          onClick={onRemoveSelected}
          data-cy="button-search-remove-many"
        />

        <div className={cls.listActionsSection}>
          <p className={cls.listActionsLight}>
            Showing {items.length} of {total}
          </p>

          <form onSubmit={onSearchByName}>
            <InputGroup
              name="batchSearchName"
              placeholder="Search by name..."
              rightElement={
                <Button minimal type="submit" icon="search" disabled={isFindingRequests} />
              }
              value={form.name}
              onChange={onNameChange}
            />
          </form>
        </div>
      </div>

      <Table
        className={cls.list}
        schema={schema}
        items={items}
        interactive
        selectable
        isRowSelected={isRowSelected}
        loading={isFindingRequests}
        loadingCaption="Loading searches..."
        emptyStateProps={{
          title: 'No searches',
          description: 'You have not started any searches yet.',
          action: (
            <>
              <Button
                large
                intent={Intent.PRIMARY}
                disabled={!creditOverview || creditOverview.balance <= 0n}
                text="Start a new search"
                icon="search-around"
                onClick={onCreateBatch}
              />
              <AnchorButton
                large
                href={docsUrl}
                text="See API User Guide"
                icon="document"
                target="_blank"
              />
            </>
          ),
        }}
        onRowClick={onNavigateToItem}
        onRowSelectionChange={toggleItemSelection}
        onAllRowsSelectionChange={toggleAllItemsSelection}
        onHover={handleHover}
        onMouseLeave={handleMouseLeave}
        hoveredRow={hoveredRow}
        data-cy="table-searches"
      >
        {start + size < total && (
          <Table.After className={cls.footer}>
            <Button
              large
              minimal
              disabled={isFindingRequests}
              loading={isFindingRequests}
              text="Show more searches"
              icon="double-chevron-down"
              rightIcon="double-chevron-down"
              onClick={onFindNextItems}
            />
          </Table.After>
        )}
      </Table>
    </div>
  );
}

BatchRequestListPage.propTypes = {
  items: PropTypes.array,
  pagination: PropTypes.object,
  findNextItems: PropTypes.func,
};

function mapStateToProps(state) {
  const requestState = state.batchSearch.request;
  const data = requestState.data;

  return {
    data,
  };
}

export default flowRight([
  connect(mapStateToProps, { findRequests, removeRequests }),
  withDataActions({
    mapInputProps: props => ({
      find: props.findRequests,
      remove: props.removeRequests,
    }),
    itemsChunkSize: 20,
  }),
  withSelectability(),
])(BatchRequestListPage);
